/**
 * /el/guides/proypologismos-gamou
 *
 * The Greek counterpart of `how-to-create-wedding-budget.en.js`.
 *
 * ── The one thing this article deliberately is not ───────────────────────
 * It is not «πόσο κοστίζει ένας γάμος». That is the dominant Greek query on
 * this subject and it is the wrong one to answer: any figure we published would
 * be invented, and the brief that commissioned this translation says so
 * outright. Greek wedding costs are not measured anywhere we can cite, and a
 * plausible-sounding average is worse than no number at all, because a reader
 * will plan against it.
 *
 * So the article says in its opening what it does NOT contain, and then does
 * the thing that is genuinely useful and that nobody else on the Greek SERP
 * does: it teaches the distinction between δεσμευμένα, πληρωμένα and what is
 * actually left. There is not a single Greek price, average, range or market
 * figure anywhere in this file, and none may be added. The euro amounts in the
 * worked example are ARITHMETIC — they are labelled as such twice, in the copy
 * and in a note, exactly as the English module labels its own.
 *
 * ── Greek rather than translated ─────────────────────────────────────────
 *  - **Η συνεισφορά των γονιών** is the money question of a Greek wedding, and
 *    the English section on it is short because it is not. It keeps the English
 *    rule — count what is confirmed, treat a warm sentence at dinner as a
 *    possible improvement — and adds the practical Greek follow-up: agree what
 *    a contribution is FOR, because a contribution attached to a category is a
 *    contribution attached to a decision.
 *  - **Οι κατηγορίες** include τα έξοδα του μυστηρίου, and στέφανα, λαμπάδες
 *    and μπομπονιέρες live in the small-costs line where a Greek couple will
 *    actually meet them.
 *  - **Το κτήμα με ελάχιστη κατανάλωση** is the local form of the "minimum
 *    spend" trap in the guest-count section.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 *   - No percentage split presented as a rule. The reserve range is given as
 *     «συνήθως προτείνεται» and immediately called a starting point.
 *   - No Greek cost figures of any kind. See above.
 *   - Not financial advice, and a note says so.
 *   - «Συνήθως» is doing real work in the per-head section and must survive:
 *     a venue may charge flat, or set a minimum that does not fall when you
 *     invite fewer people.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * All in the closing section and all already published on /el/wedding-planning:
 * the categories, a cost with its label, amount, due date, category and vendor,
 * the budget page's four numbers with a flag when commitments pass the total,
 * dated payments on the calendar, and Éros reading those totals and making a
 * change when asked.
 *
 * And the denials: not accounting software, not financial advice, no guarantee
 * of staying inside a budget, no reminders about vendor payments, and no
 * substitute for the couple's own contracts and records. Éros cannot set the
 * budget and will not decide what they spend.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const howToCreateWeddingBudgetEl = {
  slug: "proypologismos-gamou",

  title: "Προϋπολογισμός γάμου: πώς να τον οργανώσετε και να τον κρατήσετε",
  metaTitle: "Προϋπολογισμός γάμου: πώς να τον οργανώσετε | Agapaé",
  metaDescription:
    "Πώς φτιάχνεται ένας προϋπολογισμός γάμου που αντέχει: τι μπορείτε να ξοδέψετε, κατηγορίες, εκτιμήσεις και προσφορές, και η διαφορά ανάμεσα σε δεσμευμένα και πληρωμένα.",

  eyebrow: "Οργάνωση γάμου",

  hero: {
    src: "/assets/images/invitation/invitation-1.jpg",
    alt: "Φωτεινή ασπρόμαυρη φωτογραφία ζευγαριού που στέκεται δίπλα δίπλα, με το κάδρο να ξεκινά από τους ώμους και κάτω, και το χέρι της να ακουμπά στο μανίκι του σμόκιν καθώς κρατά ένα μικρό μπουκέτο με ανοιχτόχρωμα λουλούδια",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Οι περισσότεροι προϋπολογισμοί γάμου ξεκινούν ως ένας αριθμός που ειπώθηκε δυνατά. Για ένα διάστημα κάνει τη δουλειά του: αποκλείει χώρους, επιτρέπει άλλους, και κάνει το όλο πράγμα να μοιάζει εφικτό.`,
    `Η δυσκολία έρχεται λίγους μήνες μετά, όταν έχουν κλείσει τρεις τέσσερις συνεργάτες, έχουν φύγει δύο προκαταβολές, και κάποιος ρωτά πόσα έμειναν. Οι ειλικρινείς απαντήσεις είναι δύο, και απέχουν χιλιάδες, γιατί τα χρήματα που έχετε πληρώσει και τα χρήματα που έχετε υποσχεθεί δεν είναι το ίδιο πράγμα.`,
    `Αυτός ο οδηγός δεν σας λέει πόσο κοστίζει ένας γάμος. Δεν υπάρχει τέτοιος αριθμός, και όποιος σας τον δώσει τον έχει βγάλει από αλλού.`,
    `Είναι για τον προϋπολογισμό που μπορεί να απαντήσει στη δική σας ερώτηση: τι μπορείτε να ξοδέψετε, τι περιμένετε να ξοδέψετε, σε τι έχετε δεσμευτεί, τι έχει πληρωθεί, τι μένει, και μπροστά σε ποια απόφαση σας αφήνουν όλα αυτά.`,
  ],

  sections: [
    {
      heading: "Ο προϋπολογισμός είναι εργαλείο απόφασης",
      content: [
        `Δεν είναι μια λίστα με το τι κοστίζει ένας γάμος. Είναι αυτό που κοιτάτε πριν πείτε ναι σε κάτι, και η αξία του βρίσκεται στις ερωτήσεις που απαντά επιτόπου:`,
        {
          type: "list",
          items: [
            `Το αντέχουμε αυτό, με όσα έχουμε ήδη συμφωνήσει;`,
            `Τι θα έπρεπε να κόψουμε για να πούμε ναι;`,
            `Πόσα από τα χρήματά μας είναι ήδη δεσμευμένα;`,
            `Πόσα είναι πραγματικά ακόμη δικά μας να τα μοιράσουμε;`,
            `Ποια είναι η επόμενη απόφαση που κοστίζει στ' αλήθεια;`,
          ],
        },
        `Ένα σύνολο και μερικές κατηγορίες δεν τις απαντούν. Χρειάζονται χρήματα παρακολουθημένα σε όλη τους τη διαδρομή, και η διαδρομή έχει σειρά:`,
        {
          type: "steps",
          items: [
            `Τα χρήματα που έχετε πραγματικά διαθέσιμα.`,
            `Το σύνολο που αποφασίζετε ότι μπορεί να κοστίσει ο γάμος.`,
            `Οι κατηγορίες στις οποίες το μοιράζετε.`,
            `Μια εκτίμηση για την καθεμία, πριν ξέρετε οτιδήποτε.`,
            `Μια προσφορά, μόλις σας δώσει πραγματική τιμή ένας πραγματικός συνεργάτης.`,
            `Ένα δεσμευμένο κόστος, μόλις συμφωνήσετε.`,
            `Όσα έχετε πληρώσει μέχρι τώρα.`,
            `Όσα μένουν να πληρωθούν πάνω σε όσα έχετε δεσμευτεί.`,
            `Όσα μένουν αδέσμευτα, και η επόμενη απόφαση που πρέπει να καλύψουν.`,
          ],
        },
        `Σχεδόν κάθε προϋπολογισμός που παύει να είναι χρήσιμος σταμάτησε κάπου γύρω στο τέταρτο βήμα. Ο υπόλοιπος οδηγός είναι τα άλλα πέντε.`,
      ],
    },
    {
      heading: "Ξεκινήστε από τα χρήματα, όχι από τις κατηγορίες",
      content: [
        `Ένας προϋπολογισμός χτισμένος πάνω στο τι «πρέπει» να κοστίζει ένας γάμος είναι ο προϋπολογισμός κάποιου άλλου. Χτίστε τον δικό σας από τα χρήματα που θα υπάρχουν όταν πέσουν οι λογαριασμοί.`,
        `Τρία είδη προστίθενται εδώ, και δεν θα έπρεπε να μπερδεύονται:`,
        { type: "subheading", text: "Όσα έχετε ήδη" },
        `Ό,τι είναι στην άκρη σήμερα. Είναι το μόνο σίγουρο κομμάτι, και το μόνο πάνω στο οποίο είναι ασφαλές να δεσμευτείτε νωρίς.`,
        { type: "subheading", text: "Όσα θα προσθέσετε" },
        `Ό,τι μπορείτε ρεαλιστικά να βάλετε στην άκρη μέχρι τον γάμο: ένα ποσό ανά μήνα επί τους μήνες που πραγματικά έχετε, όχι ένα αισιόδοξο συνολικό νούμερο. Και μην ξεχνάτε ότι οι προκαταβολές φεύγουν μέσα στο ίδιο διάστημα.`,
        { type: "subheading", text: "Όσα έχουν απλώς ειπωθεί" },
        `Η συνεισφορά των γονιών κυμαίνεται από ένα επιβεβαιωμένο ποσό μέχρι μια ζεστή κουβέντα σε ένα τραπέζι. Μετρήστε το πρώτο. Το δεύτερο δείτε το ως πιθανή βελτίωση και όχι ως μέρος του πλάνου· κι αν γίνει αληθινό, αποφασίστε τότε τι αγοράζει.`,
        `Και μια κουβέντα που αξίζει να γίνει νωρίς, γιατί στην Ελλάδα γίνεται σχεδόν πάντα αργά: συμφωνήστε σε τι πηγαίνει η κάθε συνεισφορά. Ένα ποσό δεμένο με μια κατηγορία είναι ένα ποσό δεμένο με μια απόφαση, και ξεκαθαρίζει ποιος αποφασίζει τι πολύ πριν γίνει ζήτημα.`,
        `Προσθέστε τα δύο πρώτα συν ό,τι είναι επιβεβαιωμένο από το τρίτο: αυτό είναι το ταβάνι σας, και κάθε επόμενη απόφαση μετριέται πάνω σε αυτό.`,
        {
          type: "note",
          text: `Αυτός ο οδηγός περιγράφει έναν τρόπο να παρακολουθείτε χρήματα, όχι τι να ξοδέψετε ή πώς να το χρηματοδοτήσετε. Οι αποφάσεις για αποταμίευση, δανεισμό ή οικογενειακή συνεισφορά είναι δικές σας, και αξίζουν κατάλληλη συμβουλή αν είναι σημαντικές.`,
        },
      ],
    },
    {
      heading: "Αφήστε περιθώριο πριν μοιράσετε τα πάντα",
      content: [
        `Κρατήστε κάτι στην άκρη πριν μοιράσετε οτιδήποτε — όχι επειδή οι γάμοι είναι μυστηριωδώς απρόβλεπτοι, αλλά για δύο συνηθισμένους λόγους: κόστη που εκτιμήθηκαν πριν υπάρξει προσφορά, και μικρές αγορές που δεν έγραψε κανείς πουθενά.`,
        `Ένα απόθεμα τα απορροφά χωρίς συζήτηση. Χωρίς αυτό, τα ίδια χρήματα θα ξοδευτούν ούτως ή άλλως, από όποια κατηγορία έχει ακόμη χώρο, συνήθως αργά και βιαστικά.`,
        `Το πόσο είναι θέμα κρίσης, όχι τύπου. Συνήθως προτείνεται να κρατάτε κάτι στην περιοχή του πέντε με δέκα τοις εκατό — ως αφετηρία και όχι ως κανόνα, αφού ένας γάμος με τα περισσότερα κόστη ήδη κοστολογημένα χρειάζεται λιγότερο περιθώριο από έναν που ακόμη φαντάζεστε.`,
        `Κρατήστε το ως δική του, ορατή γραμμή και όχι ως χαλαρότητα κρυμμένη σε κάθε κατηγορία. Ένα απόθεμα που βλέπετε είναι ένα απόθεμα που μπορείτε να αποφασίσετε να ξοδέψετε.`,
      ],
    },
    {
      heading: "Διαλέξτε τις κατηγορίες με τις οποίες θα αποφασίζετε",
      content: [
        `Οι κατηγορίες δεν είναι λογιστική άσκηση. Δουλειά τους είναι να κάνουν ορατό έναν συμβιβασμό: δύο πράγματα δίπλα δίπλα, ώστε τα χρήματα να μετακινούνται συνειδητά.`,
        `Οι περισσότεροι γάμοι είναι κάποιο υποσύνολο από:`,
        {
          type: "list",
          items: [
            `τον χώρο ή το κτήμα`,
            `το φαγητό και το ποτό`,
            `τη φωτογράφιση`,
            `τη βιντεοσκόπηση`,
            `τη μουσική και το DJ`,
            `τα λουλούδια και τη διακόσμηση`,
            `το νυφικό και το κοστούμι`,
            `μαλλιά, μακιγιάζ και περιποίηση`,
            `τα προσκλητήρια και τα έντυπα`,
            `τις μετακινήσεις`,
            `wedding planner ή συντονιστή της ημέρας`,
            `ενοικιάσεις, όπως έπιπλα, σερβίτσια ή φωτισμό`,
            `διαμονή και πρακτικά για τους καλεσμένους, όπου τα καλύπτετε`,
            `τα έξοδα του μυστηρίου, όπως η εκκλησία και τα χαρτιά`,
            `όλα τα μικρά — στέφανα, λαμπάδες, μπομπονιέρες — και το απόθεμα`,
          ],
        },
        `Κανένας γάμος δεν χρειάζεται όλες, και μια άδεια κατηγορία είναι μια γραμμή που προσπερνάτε. Προσθέστε μία όταν υπάρχει μέσα της πραγματική απόφαση.`,
        `Η αντίστροφη αστοχία είναι πιο συχνή: να τα σπάσετε τόσο ψιλά ώστε κάθε ερώτηση να θέλει τρεις γραμμές ενημέρωση. Αν δύο πράγματα αποφασίζονται πάντα μαζί, είναι μία κατηγορία. Αν μια γραμμή κρύβει μια απόφαση που κάνετε ξανά και ξανά, χωρίστε τη.`,
      ],
    },
    {
      heading: "Μοιράστε με βάση τις δικές σας προτεραιότητες",
      content: [
        `Τρία ζευγάρια με το ίδιο σύνολο μπορούν να βγάλουν εντελώς διαφορετικούς προϋπολογισμούς και να έχουν και τα τρία δίκιο. Το ένα βάζει το ένα τρίτο στο φαγητό. Το άλλο κλείνει πρώτα τον φωτογράφο και χτίζει γύρω του. Το τρίτο ξοδεύει στον χώρο και καλεί σαράντα ανθρώπους αντί για διακόσιους.`,
        `Ένας χρήσιμος τρόπος να ξεκινήσετε: ο καθένας σας ονομάζει δύο τρία πράγματα που θα φτώχαιναν πραγματικά τη μέρα αν έλειπαν. Η τομή είναι εκεί που πάνε τα χρήματα· οι διαφωνίες είναι η κουβέντα που αξίζει να γίνει νωρίς, όσο οι κατανομές μετακινούνται φθηνά.`,
        `Οι ποσοστιαίες κατανομές υπάρχουν σε κάθε άρθρο για γάμους, και είναι αφετηρία και όχι κανόνας. Δεν μπορούν να ξέρουν τον αριθμό των καλεσμένων σας, τον τόπο, αν θα χρειαστεί να πετάξουν οι μισοί, τι περιλαμβάνει ο χώρος σας ή τι χρεώνουν οι συνεργάτες κοντά σας — και το καθένα από αυτά μπορεί να μετακινήσει μια κατηγορία κατά το μισό.`,
        `Χρησιμοποιήστε μία για να σπάσετε τη λευκή σελίδα, και μετά αφήστε τις δικές σας προσφορές να τη γράψουν από πάνω.`,
        `Κρατήστε την αριθμητική τίμια: οι κατηγορίες πρέπει να αθροίζουν στο σύνολο, μαζί με το απόθεμα. Κάθε κατανομή έχει δύο μισά, και το να ονομάσετε το δεύτερο — τι παίρνει λιγότερα — είναι που την κάνει απόφαση αντί για ευχή.`,
      ],
    },
    {
      heading: "Πρώτα εκτιμήσεις, μετά πραγματικοί αριθμοί",
      content: [
        `Θα βάλετε αριθμούς πριν σας δώσει κανείς έναν. Έτσι ξεκινά ένας προϋπολογισμός. Σημασία έχει κάθε αριθμός να λέει τι είδους είναι.`,
        { type: "subheading", text: "Η εκτίμηση" },
        `Αυτό που νομίζετε αυτή τη στιγμή ότι μπορεί να κοστίζει κάτι — από ένα δημοσιευμένο εύρος, από αυτό που πλήρωσε ένας φίλος, ή από μια εικασία που θα αναθεωρήσετε. Επιτρέπει στην αριθμητική να δουλέψει, και δεν αξίζει τίποτα ως υπόσχεση.`,
        { type: "subheading", text: "Η προσφορά" },
        `Αυτό που λέει ένας συγκεκριμένος συνεργάτης ότι θα κοστίσει, για συγκεκριμένο αντικείμενο, συνήθως για περιορισμένο χρόνο. Καλύτερη πληροφορία, και πάλι όχι δέσμευση: μέχρι να την αποδεχτείτε δεν χρωστάτε σε κανέναν, και μπορεί να λήξει.`,
        `Καθώς προχωρά η έρευνα, οι εκτιμήσεις αντικαθίστανται από προσφορές και ο προϋπολογισμός γίνεται ακριβέστερος χωρίς να ξαναχτιστεί. Κρατήστε την εκτίμηση δίπλα στην προσφορά: η διαφορά είναι η είδηση, και μια κατηγορία που βγαίνει συνέχεια πιο ακριβή σάς λέει κάτι για ολόκληρο το πλάνο.`,
        `Όταν δεχτείτε μια προσφορά, αλλάζει το είδος του αριθμού:`,
        {
          type: "steps",
          items: [
            `Εκτίμηση: αριθμός που διαλέξατε, αλλάζει χωρίς κόστος.`,
            `Προσφορά: αριθμός που σας έδωσε συνεργάτης, ακόμη δικό σας να τον αρνηθείτε.`,
            `Δεσμευμένο: αριθμός στον οποίο συμφωνήσατε, και πλέον οφείλετε.`,
          ],
        },
        {
          type: "p",
          text: [
            `Από εκεί και πέρα, πηγή αλήθειας είναι η συμφωνία και όχι ο προϋπολογισμός: πάρτε το δεσμευμένο ποσό και τις ημερομηνίες πληρωμής από αυτό που κλείσατε. Το `,
            {
              text: "Συνεργάτες και πληρωμές γάμου: πώς να τα παρακολουθείτε",
              href: "/guides/how-to-track-wedding-vendors-payments",
            },
            ` καλύπτει το πώς κρατιέται μαζεμένη αυτή η λεπτομέρεια ανά συνεργάτη.`,
          ],
        },
      ],
    },
    {
      heading: "Δεσμευμένα, πληρωμένα, και τι μένει πραγματικά",
      content: [
        `Εδώ ένας προϋπολογισμός είτε δουλεύει είτε παύει αθόρυβα να λέει την αλήθεια. Τρεις αριθμοί, κρατημένοι χωριστά:`,
        { type: "subheading", text: "Δεσμευμένα" },
        `Όσα έχετε συμφωνήσει, είτε έχει πληρωθεί κάτι είτε όχι. Μια υπογεγραμμένη συμφωνία 3.000 ευρώ είναι δέσμευση 3.000 ευρώ από τη μέρα που υπογράφεται, όχι από τη μέρα που φεύγει η τελευταία δόση.`,
        { type: "subheading", text: "Πληρωμένα" },
        `Όσα έχουν πραγματικά φύγει από τον λογαριασμό σας. Όχι κανονισμένα, όχι υποσχεμένα, όχι έτοιμα να φύγουν.`,
        { type: "subheading", text: "Υπόλοιπο" },
        `Δεσμευμένα μείον πληρωμένα: χρήματα που ήδη οφείλετε και δεν έχετε δώσει. Ο λιγότερο ορατός αριθμός στους περισσότερους προϋπολογισμούς, και αυτός που τους καταστρέφει.`,
        `Πάρτε έναν ενδεικτικό γάμο: σύνολο 30.000 ευρώ, υπογεγραμμένες συμφωνίες 24.000 ευρώ, και 8.000 ευρώ πληρωμένα.`,
        {
          type: "steps",
          items: [
            `Συνολικός προϋπολογισμός: 30.000 — όσα αποφάσισαν ότι μπορούν να ξοδέψουν.`,
            `Δεσμευμένα: 24.000 — συμφωνίες που έχουν ήδη γίνει.`,
            `Πληρωμένα: 8.000 — χρήματα που έχουν όντως φύγει.`,
            `Υπόλοιπο σε αυτές τις δεσμεύσεις: 16.000 — οφειλόμενα, όχι προαιρετικά.`,
            `Αδέσμευτος προϋπολογισμός: 6.000 — το μόνο κομμάτι που είναι ακόμη δικό τους να αποφασίσουν.`,
          ],
        },
        `Και τώρα η αφαίρεση που κάνει τη ζημιά. Συνολικός προϋπολογισμός μείον πληρωμένα κάνει 22.000, και μοιάζει με την απάντηση στο «πόσα μας έμειναν;». Δεν είναι: 16.000 από αυτά τα οφείλουν ήδη σε ανθρώπους με τους οποίους έχουν συμφωνήσει.`,
        `Ο αριθμός που απαντά στην ερώτηση είναι συνολικός προϋπολογισμός μείον δεσμευμένα — 6.000, λίγο περισσότερο από το ένα τέταρτο του αριθμού που φαινόταν σωστός. Το να μπερδεύονται τα δύο είναι ο τρόπος με τον οποίο ένας προϋπολογισμός μένει άνετος μέχρι τον τελευταίο μήνα.`,
        `Κρατήστε και τα δύο. Το τι μένει να πληρωθεί σας λέει τι θα φύγει από τον λογαριασμό σας και περίπου πότε. Το τι μένει αδέσμευτο σας λέει σε τι μπορείτε ακόμη να πείτε ναι.`,
        {
          type: "note",
          text: `Τα ποσά παραπάνω δείχνουν την αριθμητική. Δεν είναι προτεινόμενος ούτε τυπικός προϋπολογισμός γάμου: οι γάμοι διαφέρουν τεράστια σε αριθμό καλεσμένων, τόπο, εποχή και προτεραιότητες.`,
        },
      ],
    },
    {
      heading: "Τα κόστη που κινούνται με τον αριθμό των καλεσμένων",
      content: [
        `Ένα μέρος του γάμου κοστολογείται ανά άτομο και ένα άλλο όχι. Το να ξέρετε ποιο είναι ποιο μετατρέπει τη λίστα καλεσμένων από συναισθηματικό ερώτημα σε οικονομικό.`,
        `Κινούνται συνήθως με τον αριθμό: το φαγητό και το ποτό, τα σερβίτσια και οι ενοικιάσεις, τα προσκλητήρια, οι μπομπονιέρες, οι μετακινήσεις που καλύπτετε και όποια διαμονή αναλαμβάνετε.`,
        `Συνήθως δεν κινούνται: η φωτογράφιση και το βίντεο, η μουσική, ο wedding planner, το νυφικό και το κοστούμι, τα μαλλιά και το μακιγιάζ, και πολύ συχνά η ίδια η ενοικίαση του χώρου.`,
        `Το «συνήθως» εκεί κάνει πραγματική δουλειά. Ένας χώρος μπορεί να χρεώνει σταθερά μέχρι μια χωρητικότητα, ή να ορίζει ελάχιστη κατανάλωση που δεν πέφτει επειδή καλέσατε λιγότερους. Δείτε τις δικές σας προσφορές αντί να υποθέσετε.`,
        `Και δοκιμάστε το προς τις δύο κατευθύνσεις. Το «αν προσθέταμε είκοσι καλεσμένους, ποιοι αριθμοί αλλάζουν;» είναι η πραγματική τιμή μιας μεγαλύτερης λίστας. Το «αν αφαιρούσαμε είκοσι, ποιοι αριθμοί όντως πέφτουν;» είναι η δυσκολότερη ερώτηση: οι ελάχιστες καταναλώσεις μένουν, οι προκαταβολές έχουν φύγει, και μια παραγγελία που τυπώθηκε δεν μικραίνει.`,
        {
          type: "p",
          text: [
            `Και τα δύο θέλουν έναν αριθμό που εμπιστεύεστε, ενημερωμένο καθώς έρχονται οι απαντήσεις. Το `,
            {
              text: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` καλύπτει το χτίσιμο και τη συντήρησή του.`,
          ],
        },
      ],
    },
    {
      heading: "Τα κόστη που ξεφεύγουν εύκολα",
      content: [
        `Οι προϋπολογισμοί σπάνια σπάνε από τις μεγάλες γραμμές, που κοστολογούνται, συζητιούνται και παρακολουθούνται. Σπάνε από είκοσι μικρά πράγματα, το καθένα πολύ μικρό για να γραφτεί.`,
        `Όσα έρχονται συνήθως μετά την πρώτη τιμή:`,
        {
          type: "list",
          items: [
            `μεταποιήσεις και πρόβες, καμιά φορά περισσότερες από μία`,
            `χρεώσεις μεταφοράς, στησίματος, μαζέματος και μετακίνησης`,
            `χρεώσεις υπηρεσίας και φόροι, όπου η προσφορά τα αναφέρει χωριστά`,
            `υπερωρίες, αν η βραδιά ξεπεράσει τις ώρες που κλείσατε`,
            `επιπλέον ενοικιάσεις που ανακαλύπτονται όσο στήνεται η διάταξη`,
            `ταχυδρομικά, επιπλέον εκτυπώσεις και ανατυπώσεις`,
            `παρκάρισμα, άδειες ή χρεώσεις πρόσβασης στον χώρο`,
            `φαγητό για τους συνεργάτες που είναι μαζί σας όλη μέρα`,
            `δώρα, αξεσουάρ και οι μικροαγορές του τελευταίου δεκαπενθημέρου`,
          ],
        },
        `Δεν είναι εξαντλητική λίστα, και καμία δεν θα ήταν. Σημασία έχει η συνήθεια: μία ερώτηση σε κάθε προσφορά πριν την αποδεχτείτε — τι δεν είναι μέσα σε αυτόν τον αριθμό; Τα περιλαμβανόμενα, οι εξαιρέσεις και οι όροι συνήθως το απαντούν.`,
        `Και δώστε στα μικρά ένα σπίτι: μία γραμμή «διάφορα» που την ενημερώνετε τίμια αξίζει περισσότερο από δεκαπέντε κατηγορίες που δεν συντηρεί κανείς.`,
      ],
    },
    {
      heading: "Ο προϋπολογισμός δεν είναι η καταγραφή των συνεργατών",
      content: [
        `Οι δύο καταγραφές συγχέονται σχεδόν πάντα, και όμως απαντούν σε τελείως διαφορετικά ερωτήματα.`,
        { type: "subheading", text: "Ο προϋπολογισμός" },
        `Πώς χωράει ο γάμος συνολικά μέσα στα χρήματα που έχετε. Οργανωμένος ανά κατηγορία, και τον διαβάζετε όταν αποφασίζετε αν αντέχετε κάτι.`,
        { type: "subheading", text: "Η καταγραφή των συνεργατών" },
        `Τι κοστίζει ο καθένας, τι συμφωνήθηκε, τι έχει πληρωθεί και τι πέφτει μετά. Οργανωμένη ανά δέσμευση, και τη διαβάζετε όταν βλέπετε τι πρέπει να γίνει αυτή την εβδομάδα.`,
        `Συναντιούνται σε έναν αριθμό: το δεσμευμένο κόστος. Κάθε συμφωνία που κλείνετε είναι μια γραμμή στην καταγραφή και μια δέσμευση στον προϋπολογισμό, και αν τα δύο διαφωνούν, κάποιο δεν ενημερώθηκε.`,
        `Και η αστοχία που αξίζει να ονομαστεί: ένας προϋπολογισμός που τρέφεται μόνο από τον τραπεζικό λογαριασμό. Μετράει ό,τι πληρώθηκε, δείχνει άνετο περιθώριο, και δεν λέει τίποτα για τις υπογεγραμμένες συμφωνίες πίσω του.`,
      ],
    },
    {
      heading: "Τι δείχνει ένας προϋπολογισμός που δουλεύει",
      content: [
        `Ένας προϋπολογισμός που κουβαλά την αλυσίδα αυτού του οδηγού χρειάζεται περίπου δέκα πράγματα ανά γραμμή:`,
        {
          type: "list",
          items: [
            `την κατηγορία στην οποία ανήκει`,
            `τον συνεργάτη ή το αντικείμενο`,
            `το εκτιμώμενο κόστος`,
            `το κόστος της προσφοράς, μόλις υπάρξει`,
            `το δεσμευμένο κόστος, μόλις συμφωνήσετε`,
            `όσα έχουν πληρωθεί`,
            `όσα μένουν πάνω σε αυτό`,
            `την ημερομηνία της επόμενης πληρωμής, όπου υπάρχει`,
            `μια κατάσταση: εκτίμηση, προσφορά, δεσμευμένο ή εξοφλημένο`,
            `μια σημείωση για ό,τι δεν θα είναι προφανές σε έξι μήνες`,
          ],
        },
        `Μία γραμμή, δουλεμένη ως το τέλος: η φωτογράφιση. Εκτίμηση 2.500 ευρώ πριν μιλήσετε σε κανέναν. Προσφορά 3.000 από τον φωτογράφο που θέλατε. Δεσμευμένα 3.000 όταν κλείσατε. Πληρωμένα 600. Υπόλοιπο 2.400, με την επόμενη πληρωμή να έχει ημερομηνία.`,
        `Διαβάζοντάς τη, ξέρετε τα πάντα: βγήκε 500 πάνω από την εκτίμηση, αυτά τα 500 πρέπει να έρθουν από κάπου συγκεκριμένα, και 2.400 μένουν ακόμη να φύγουν. Το «φωτογράφιση, 3.000» δεν σας λέει σχεδόν τίποτα.`,
        `Και από κάτω, τα τέσσερα σύνολα: δεσμευμένα, πληρωμένα, υπόλοιπο προς πληρωμή, και προϋπολογισμός μείον δεσμευμένα.`,
        `Το τι τα κρατά ελάχιστα μετράει. Ένα Excel το κάνει μια χαρά· ένα εργαλείο οργάνωσης το κρατά δίπλα στους συνεργάτες και τις ημερομηνίες που το αφορούν· πολλά ζευγάρια χρησιμοποιούν και τα δύο. Τα ζητούμενα είναι τα ίδια: ένα σημείο που είναι η αλήθεια αντί για τρία που διαφωνούν, μια κατάσταση σε κάθε γραμμή, ενημερωμένους αριθμούς και καθαρή εικόνα για το τι έρχεται.`,
      ],
    },
    {
      heading: "Πότε τον ξανακοιτάτε",
      content: [
        `Ένας προϋπολογισμός παλιώνει από δύο μεριές: κάτι αλλάζει και δεν το ενημερώνει κανείς, ή δεν αλλάζει τίποτα και δεν τον κοιτάζει κανείς.`,
        { type: "subheading", text: "Όταν αλλάζει κάτι" },
        `Μια αναθεωρημένη προσφορά, ένα αναβαθμισμένο πακέτο, ένας χώρος που άλλαξε, μια ημερομηνία που μετακινήθηκε, είκοσι καλεσμένοι παραπάνω. Το καθένα αγγίζει περισσότερα από τη γραμμή στην οποία πέφτει, οπότε ξαναδιαβάστε τέσσερα πράγματα: την κατηγορία του, το σύνολο των δεσμευμένων, τι μένει να πληρωθεί και τι μένει αδέσμευτο. Και μετά ρωτήστε αν κάποια προτεραιότητα πιο κάτω στη λίστα έγινε ξαφνικά μη προσιτή.`,
        `Δεν είναι ξαναχτίσιμο. Οι περισσότερες αλλαγές μετακινούν μία γραμμή και ένα σύνολο και παίρνουν πέντε λεπτά — γι' αυτό και πρέπει να γίνονται τη στιγμή της αλλαγής.`,
        { type: "subheading", text: "Ένας σύντομος, τακτικός έλεγχος" },
        {
          type: "steps",
          items: [
            `Διαβάστε το σύνολο των δεσμευμένων: όλα όσα έχετε συμφωνήσει σήμερα.`,
            `Σημειώστε ως πληρωμένες μόνο τις πληρωμές που ολοκληρώθηκαν πραγματικά.`,
            `Δείτε τι πέφτει μετά, και επιβεβαιώστε ότι τα χρήματα θα υπάρχουν.`,
            `Ενημερώστε όποια προσφορά ή συμφωνία άλλαξε από την προηγούμενη φορά.`,
            `Συγκρίνετε τι μένει να πληρωθεί με το τι μένει διαθέσιμο.`,
            `Ονομάστε την επόμενη απόφαση που θέλει συμβιβασμό, και ποιος την παίρνει.`,
          ],
        },
        `Πόσο συχνά εξαρτάται από το πόσα τρέχουν: λίγα στην αρχή, μηνιαία όσο κλείνονται συνεργάτες, πιο συχνά τις τελευταίες εβδομάδες όταν πέφτουν μαζί οι εξοφλήσεις.`,
      ],
    },
    {
      heading: "Ο προϋπολογισμός σε ένα σημείο",
      content: [
        {
          type: "note",
          text: `Ο χώρος οργάνωσης και ο Ἔρως στο Agapaé περιλαμβάνονται στο πακέτο Heirloom.`,
        },
        `Στο Agapaé η οργάνωση είναι χωρισμένη στα μέρη από τα οποία αποτελείται ένας γάμος, και το καθένα κρατά τις δικές του εργασίες, συνεργάτες, κόστη, έγγραφα και αποφάσεις. Ένα κόστος καταγράφεται περίπου όπως το περιγράφει αυτός ο οδηγός: μια περιγραφή, ένα ποσό, μια ημερομηνία, η κατηγορία του και ο συνεργάτης από τον οποίο ήρθε, με το καθένα είτε να εκκρεμεί είτε να έχει πληρωθεί.`,
        `Η σελίδα του προϋπολογισμού κρατά ακριβώς τη διάκριση πάνω στην οποία είναι χτισμένο αυτό το κείμενο. Ορίζετε ένα σύνολο, και δίπλα του κάθονται όσα έχουν καταγραφεί ως κόστος, όσα έχουν πληρωθεί, όσα οφείλονται ακόμη και όσα μένουν από τον προϋπολογισμό μόλις αφαιρεθεί ό,τι έχει δεσμευτεί — οι δύο απαντήσεις στο «τι μένει», ορατές μαζί, με ανάλυση ανά κατηγορία και μια επισήμανση όταν οι δεσμεύσεις ξεπεράσουν αυτό που ορίσατε. Οι πληρωμές που εκκρεμούν και έχουν ημερομηνία εμφανίζονται στο ημερολόγιο.`,
        `Ο Ἔρως ζει μέσα σε αυτόν τον χώρο. Διαβάζει αυτά τα σύνολα, απαντά από αυτά, και όταν του το ζητήσετε κάνει την αλλαγή: καταγράφει ένα κόστος, σημειώνει μια πληρωμή, προσθέτει την εργασία που ακολουθεί. Προτείνει και αποφασίζετε εσείς — δεν μπορεί να ορίσει τον προϋπολογισμό σας και δεν θα αποφασίσει τι θα ξοδέψετε.`,
        `Τι δεν είναι: λογιστικό πρόγραμμα, οικονομική συμβουλή, ή εγγύηση ότι θα μείνετε μέσα στον προϋπολογισμό. Δεν στέλνει υπενθυμίσεις για τις πληρωμές των συνεργατών, και δεν αντικαθιστά τα συμφωνητικά σας, τα παραστατικά σας ή τη δική σας καταγραφή για το τι έχει φύγει από τον λογαριασμό σας.`,
        {
          type: "p",
          text: [
            `Η οργάνωση περιγράφεται στη σελίδα `,
            { text: "Οργάνωση γάμου", href: "/wedding-planning" },
            `. Ο προϋπολογισμός είναι ένα κομμάτι ενός μεγαλύτερου πλάνου, και το `,
            {
              text: "Checklist γάμου: τι να οργανώσετε και πότε",
              href: "/guides/wedding-planning-checklist",
            },
            ` καλύπτει τη σειρά με την οποία γίνονται τα υπόλοιπα.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Πώς ξεκινάω τον προϋπολογισμό του γάμου;`,
      a: `Από τα χρήματα και όχι από τις κατηγορίες. Προσθέστε ό,τι έχετε στην άκρη, ό,τι μπορείτε ρεαλιστικά να βάλετε μέχρι τον γάμο και όποια συνεισφορά έχει επιβεβαιωθεί: αυτό είναι το ταβάνι σας. Κρατήστε ένα απόθεμα, μοιράστε τα υπόλοιπα στις κατηγορίες που θα έχει πραγματικά ο γάμος σας, και βάλτε εκτιμήσεις σημειωμένες ως εκτιμήσεις.`,
    },
    {
      q: `Τι κατηγορίες πρέπει να έχει;`,
      a: `Μόνο αυτές μέσα στις οποίες θα πάρετε αποφάσεις. Οι περισσότεροι γάμοι έχουν κάποιον συνδυασμό από χώρο, φαγητό και ποτό, φωτογράφιση, βίντεο, μουσική, λουλούδια, ρούχα, περιποίηση, έντυπα, μετακινήσεις, ενοικιάσεις, τα έξοδα του μυστηρίου, και μια γραμμή για τα μικρά και το απόθεμα.`,
    },
    {
      q: `Να μοιράσω τον προϋπολογισμό με ποσοστά;`,
      a: `Μόνο ως τρόπο να σπάσετε τη λευκή σελίδα. Ένα ποσοστό δεν ξέρει τον αριθμό των καλεσμένων σας, τον τόπο, τι περιλαμβάνει ο χώρος σας ή τι σας νοιάζει πραγματικά — και καθένα από αυτά μπορεί να μετακινήσει μια κατηγορία κατά το μισό. Αφήστε τις δικές σας προσφορές να το γράψουν από πάνω.`,
    },
    {
      q: `Τι διαφέρουν τα δεσμευμένα από τα πληρωμένα;`,
      a: `Τα προϋπολογισμένα είναι όσα σχεδιάσατε να ξοδέψετε. Τα δεσμευμένα είναι όσα έχετε συμφωνήσει, είτε έχει πληρωθεί κάτι είτε όχι. Τα πληρωμένα είναι όσα έχουν φύγει. Το κενό ανάμεσά τους είναι χρήματα που ήδη οφείλετε — γι' αυτό ο προϋπολογισμός μείον τα πληρωμένα υπερεκτιμά αυτό που σας μένει.`,
    },
    {
      q: `Πόσο συχνά τον ελέγχω;`,
      a: `Όποτε αλλάζει κάτι, και με έναν ελαφρύ ρυθμό ενδιάμεσα. Μια νέα προσφορά, ένα αναβαθμισμένο πακέτο ή μια αλλαγή στον αριθμό των καλεσμένων μπαίνουν την ίδια μέρα, γιατί μετακινούν τα δεσμευμένα. Κατά τα άλλα, μηνιαία στη μέση της οργάνωσης και συχνότερα στις τελευταίες εβδομάδες.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: [
      "how-to-track-wedding-vendors-payments",
      "wedding-planning-spreadsheet-what-to-track",
    ],

    product: [
      {
        href: "/wedding-planning",
        label: "Οργάνωση γάμου",
        blurb:
          "Δείτε πώς ο χώρος οργάνωσης κρατά τον προϋπολογισμό σας δίπλα στους συνεργάτες, τις εργασίες και τα έγγραφα που πληρώνει, με τα δεσμευμένα, τα πληρωμένα και το υπόλοιπο σε ένα ιδιωτικό σημείο. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default howToCreateWeddingBudgetEl;
